/*
 * CMP-020 Overflow menu — Phase A1. No dependencies. Anchored popover with role="menu" (SCR-013, SCR-014).
 *   menu.open(trigger)  shows the menu next to `trigger` (aligned to its right edge, flips above when there is no
 *                       room, clamped to the viewport), sets aria-expanded on it and focuses the first enabled item.
 *   menu.toggle(trigger) closes when already open for the same trigger, else opens (use this from host click handlers).
 *   menu.close(returnFocus = true) hides it and, by default, restores focus to the trigger (UI Kit §9).
 * Keys inside: ArrowDown/ArrowUp (wrap, skip disabled), Home/End, Escape (close + restore focus), Tab (close, focus goes
 * on from the trigger), Enter/Space (native button). Click outside closes without stealing focus.
 * An element with aria-controls="<menu id>" and aria-haspopup="menu" toggles the menu on click.
 * Events (bubbling): `nc-menu:action` {id, trigger} when an enabled item is chosen, `nc-menu:close` {trigger}.
 * A trigger with data-menu-anchor-icon aligns the menu's right edge to its <svg> glyph instead of its own box.
 * Destructive items are visually and textually distinct (danger color + the label says what is removed).
 */
(function () {
  // distance between the trigger box (44 px target) and the menu: 13 px puts the menu 23 px under a 24 px glyph (Figma 80:6912)
  var MENU_GAP = 13;

  // next enabled item index for a key, wrapping; null when the key is not a navigation key or nothing is enabled
  function getMenuIndex(key, current, disabled) {
    var count = disabled.length;
    var enabled = [];
    for (var i = 0; i < count; i++) if (!disabled[i]) enabled.push(i);
    if (!enabled.length) return null;
    if (key === 'Home') return enabled[0];
    if (key === 'End') return enabled[enabled.length - 1];
    var step = key === 'ArrowDown' ? 1 : key === 'ArrowUp' ? -1 : 0;
    if (!step) return null;
    var at = enabled.indexOf(current);
    if (at < 0) return step > 0 ? enabled[0] : enabled[enabled.length - 1];
    return enabled[(at + step + enabled.length) % enabled.length];
  }

  // top-left of a surface (w x h) anchored below `r` (trigger rect) right-aligned, flipped above, clamped to the viewport
  function placeMenu(r, w, h, vw, vh, gap) {
    var left = Math.min(Math.max(8, r.right - w), Math.max(8, vw - w - 8));
    var top = r.bottom + gap;
    if (top + h > vh - 8 && r.top - gap - h >= 8) top = r.top - gap - h;
    top = Math.min(Math.max(8, top), Math.max(8, vh - h - 8));
    return { left: left, top: top };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { getMenuIndex: getMenuIndex, placeMenu: placeMenu };
  if (typeof customElements === 'undefined' || customElements.get('nc-menu')) return;

  class NcMenu extends HTMLElement {
    connectedCallback() {
      this.surface = this.querySelector('.nc-menu__surface');
      this.trigger = null;
      this.surface.addEventListener('keydown', this.onKeydown.bind(this));
      this.surface.addEventListener('click', this.onItemClick.bind(this));
      this.onDocClick = this.onDocClick.bind(this);
      this.onViewport = this.onViewport.bind(this);
      document.addEventListener('click', this.onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    get isOpen() { return !this.surface.hidden; }

    get items() {
      return Array.prototype.slice.call(this.surface.querySelectorAll('[role="menuitem"]'));
    }

    open(trigger) {
      if (this.isOpen) this.close(false);
      this.trigger = trigger || null;
      this.surface.hidden = false;
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'true');
        this.place();
      }
      window.addEventListener('resize', this.onViewport);
      window.addEventListener('scroll', this.onViewport, true);
      var first = getMenuIndex('Home', -1, this.items.map(function (i) { return i.disabled; }));
      if (first !== null) this.items[first].focus();
      else this.surface.focus();
    }

    // anchors the surface to the trigger; false when the trigger is no longer on screen
    place() {
      var r = this.trigger.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return false;
      // a trigger bigger than its glyph (44 px target around a 15 px icon) can anchor the menu's right edge to the glyph
      var glyph = this.trigger.hasAttribute('data-menu-anchor-icon') && this.trigger.querySelector('svg');
      if (glyph) r = { top: r.top, bottom: r.bottom, right: glyph.getBoundingClientRect().right };
      var s = this.surface.getBoundingClientRect();
      var p = placeMenu(r, s.width, s.height, window.innerWidth, window.innerHeight, MENU_GAP);
      this.surface.style.left = p.left + 'px';
      this.surface.style.top = p.top + 'px';
      return true;
    }

    toggle(trigger) {
      if (this.isOpen && this.trigger === trigger) this.close(true);
      else this.open(trigger);
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      this.surface.hidden = true;
      window.removeEventListener('resize', this.onViewport);
      window.removeEventListener('scroll', this.onViewport, true);
      var trigger = this.trigger;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus !== false) trigger.focus();
      }
      this.dispatchEvent(new CustomEvent('nc-menu:close', { bubbles: true, detail: { trigger: trigger } }));
    }

    // focus inside the menu would fall to <body>: give it back to the trigger only in that case
    // scroll or resize: the menu follows its trigger and closes only when the trigger leaves the screen. Closing on any
    // scroll shut the menu 13 ms after it opened while a smooth scroll was still running (UX batida UX-18).
    onViewport() {
      if (this.trigger && this.place()) return;
      this.close(this.surface.contains(document.activeElement));
    }

    onItemClick(event) {
      var item = event.target.closest('[role="menuitem"]');
      if (!item || item.disabled) return;
      var trigger = this.trigger;
      this.dispatchEvent(new CustomEvent('nc-menu:action', { bubbles: true, detail: { id: item.getAttribute('data-action'), trigger: trigger } }));
      this.close(true);
    }

    onDocClick(event) {
      var control = event.target.closest && event.target.closest('[aria-controls="' + this.id + '"][aria-haspopup="menu"]');
      if (control) {
        if (this.isOpen && this.trigger === control) this.close(true);
        else this.open(control);
        return;
      }
      // the host-managed trigger (opened through toggle/open) handles its own clicks
      if (this.isOpen && !this.surface.contains(event.target) && !(this.trigger && this.trigger.contains(event.target))) this.close(false);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key === 'Tab') {
        // focus goes on from the trigger, so Tab leaves the menu instead of trapping
        this.close(true);
        return;
      }
      var list = this.items;
      var next = getMenuIndex(event.key, list.indexOf(document.activeElement), list.map(function (i) { return i.disabled; }));
      if (next === null) return;
      event.preventDefault();
      list[next].focus();
    }
  }

  customElements.define('nc-menu', NcMenu);
})();
